/**
 * SettingsContext - Shared state for all settings across the app
 * Provides: Profile data, Location settings, Usage/Quota data
 * Features: Parallel fetch, optimistic updates, cache
 */

import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { API_CONFIG } from '../utils/config';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { fetchWithAuthTimeout } from '../utils/fetchWithAuth';
import {
  FounderProfile,
  LocationSettings,
  parseFounderProfilePayload,
  parseLocationSettings,
} from '../utils/settingsContracts';
import {
  syncPendingIdeaMatchesReceipt,
} from '../utils/onboardingContinuity';

import logger from '../utils/logger';
interface SettingsContextType {
  // Profile
  founderProfile: FounderProfile | null;
  loadingProfile: boolean;
  profileLoaded: boolean;
  profileError: string | null;
  refreshProfile: () => Promise<void>;
  
  // Location
  locationSettings: LocationSettings;
  loadingLocation: boolean;
  locationLoaded: boolean;
  locationError: string | null;
  refreshLocation: () => Promise<void>;
  saveLocationSettings: (settings: Partial<LocationSettings>) => Promise<boolean>;
  
  // Combined
  loading: boolean;
  refreshAll: () => Promise<void>;
  
  // Prefetch (for hover)
  prefetchSettings: () => void;
  isPrefetched: boolean;
}

const defaultLocationSettings: LocationSettings = {
  geoConsent: false,
  locationMode: 'auto',
  manualLocation: '',
  detectedLocation: '',
  businessFocus: 'flexible'
};

const SettingsContext = createContext<SettingsContextType | null>(null);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();
  
  // Profile state
  const [founderProfile, setFounderProfile] = useState<FounderProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  
  // Location state
  const [locationSettings, setLocationSettings] = useState<LocationSettings>(defaultLocationSettings);
  const locationSettingsRef = useRef<LocationSettings>(defaultLocationSettings);
  const locationSaveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [locationLoaded, setLocationLoaded] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  
  // Prefetch state
  const [isPrefetched, setIsPrefetched] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const applyLocationSettings = useCallback((nextSettings: LocationSettings) => {
    locationSettingsRef.current = nextSettings;
    setLocationSettings(nextSettings);
  }, []);

  // Fetch founder profile
  const fetchFounderProfile = useCallback(async () => {
    if (!isLoggedIn) {
      setFounderProfile(null);
      setProfileLoaded(false);
      setProfileError(null);
      return;
    }
    
    setLoadingProfile(true);
    setProfileError(null);
    try {
      // Sync pending profile from onboarding if exists
      const pendingProfile = sessionStorage.getItem('pendingFounderProfile');
      if (pendingProfile) {
        try {
          const profileData = JSON.parse(pendingProfile);
          const pendingResponse = await fetchWithAuthTimeout(`${API_CONFIG.BACKEND_URL}/api/settings/founder-profile`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(profileData)
          });
          if (!pendingResponse.ok) {
            throw new Error(`Pending profile sync failed: ${pendingResponse.status}`);
          }
          sessionStorage.removeItem('pendingFounderProfile');
        } catch (err) {
          logger.warn('Failed to sync pending founder profile:', err);
        }
      }

      const ideaMatchesSyncStatus = await syncPendingIdeaMatchesReceipt(
        (receipt) => fetchWithAuthTimeout(
          `${API_CONFIG.BACKEND_URL}/api/onboarding/retain-ideas`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ receipt }),
          },
        ),
      );
      if (ideaMatchesSyncStatus === 'retry') {
        logger.warn('Pending Idea Matches remain queued for a later retry');
      }
      
      const response = await fetchWithAuthTimeout(`${API_CONFIG.BACKEND_URL}/api/settings/founder-profile`, {
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`Founder profile request failed: ${response.status}`);
      const data = await response.json();
      const payload = parseFounderProfilePayload(data);
      if (!payload) throw new Error('Founder profile response was invalid');

      setFounderProfile(payload.profile);
      setProfileLoaded(true);
    } catch (err) {
      logger.warn('Failed to fetch founder profile:', err);
      setProfileError('profileDataUnavailable');
    } finally {
      setLoadingProfile(false);
    }
  }, [isLoggedIn]);

  // Fetch location settings
  const fetchLocationSettings = useCallback(async () => {
    if (!isLoggedIn) {
      applyLocationSettings(defaultLocationSettings);
      setLocationLoaded(false);
      setLocationError(null);
      return;
    }
    
    setLoadingLocation(true);
    setLocationError(null);
    try {
      const response = await fetchWithAuthTimeout(`${API_CONFIG.BACKEND_URL}/api/settings/location`, {
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`Location settings request failed: ${response.status}`);
      const data = await response.json();
      if (!data.success || !data.settings) throw new Error('Location settings response was invalid');
      const parsedSettings = parseLocationSettings(data.settings);
      if (!parsedSettings) throw new Error('Location settings response was invalid');
      applyLocationSettings(parsedSettings);
      setLocationLoaded(true);
    } catch (err) {
      logger.warn('Failed to fetch location settings:', err);
      setLocationError('locationDataUnavailable');
    } finally {
      setLoadingLocation(false);
    }
  }, [applyLocationSettings, isLoggedIn]);

  // Save location settings with optimistic update
  const saveLocationSettings = useCallback((newSettings: Partial<LocationSettings>) => {
    const persist = async (): Promise<boolean> => {
      const previousSettings = locationSettingsRef.current;
      const optimisticSettings = parseLocationSettings({
        ...previousSettings,
        ...newSettings,
      });
      if (!optimisticSettings) {
        toast.error(t('locationSaveFailed'));
        setLocationError('locationSaveFailed');
        return false;
      }
      applyLocationSettings(optimisticSettings);

      try {
        const response = await fetchWithAuthTimeout(`${API_CONFIG.BACKEND_URL}/api/settings/location`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newSettings),
        });
        const data = await response.json();

        if (!response.ok || !data.success || !data.settings) {
          throw new Error(`Location settings save failed: ${response.status}`);
        }

        const savedSettings = parseLocationSettings(data.settings);
        if (!savedSettings) throw new Error('Location settings save response was invalid');
        applyLocationSettings(savedSettings);
        setLocationError(null);
        setLocationLoaded(true);
        return true;
      } catch (err) {
        applyLocationSettings(previousSettings);
        toast.error(t('locationSaveFailed'));
        logger.error('Failed to save location settings:', err);
        setLocationError('locationSaveFailed');
        return false;
      }
    };

    const queuedSave = locationSaveQueueRef.current.then(persist, persist);
    locationSaveQueueRef.current = queuedSave.then(() => undefined, () => undefined);
    return queuedSave;
  }, [applyLocationSettings, t]);

  // Parallel fetch all settings
  const refreshAll = useCallback(async () => {
    if (!isLoggedIn) return;
    
    await Promise.all([
      fetchFounderProfile(),
      fetchLocationSettings()
    ]);
    setHasFetched(true);
  }, [isLoggedIn, fetchFounderProfile, fetchLocationSettings]);

  // Prefetch settings (for hover)
  const prefetchSettings = useCallback(() => {
    if (!isPrefetched && isLoggedIn && !hasFetched) {
      setIsPrefetched(true);
      refreshAll();
    }
  }, [isPrefetched, isLoggedIn, hasFetched, refreshAll]);

  // Refresh individual
  const refreshProfile = useCallback(async () => {
    await fetchFounderProfile();
  }, [fetchFounderProfile]);

  const refreshLocation = useCallback(async () => {
    await fetchLocationSettings();
  }, [fetchLocationSettings]);

  // Combined loading state
  const loading = loadingProfile || loadingLocation;

  const value = useMemo(() => ({
    founderProfile,
    loadingProfile,
    profileLoaded,
    profileError,
    refreshProfile,
    locationSettings,
    loadingLocation,
    locationLoaded,
    locationError,
    refreshLocation,
    saveLocationSettings,
    loading,
    refreshAll,
    prefetchSettings,
    isPrefetched
  }), [
    founderProfile,
    loadingProfile,
    profileLoaded,
    profileError,
    refreshProfile,
    locationSettings,
    loadingLocation,
    locationLoaded,
    locationError,
    refreshLocation,
    saveLocationSettings,
    loading,
    refreshAll,
    prefetchSettings,
    isPrefetched
  ]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export default SettingsContext;
